import { useEffect, useMemo, useState } from 'react';

import { useCliJobsChecker } from 'cli/hooks';
import { ICliJob, ICliJobQuery } from 'cli/types';
import { useAgGridClientTableContext } from 'core/states';
import { AgGridDataRow } from 'core/types';
import { IQuotePartInventory } from 'quotes/types';

interface IProps {
  fetchExternalInventoryCliJob?: ICliJob | null;
}

export const useCheckInventoryPendingCliJobs = ({ fetchExternalInventoryCliJob }: IProps) => {
  const [enabledJobs, setEnabledJobs] = useState<{ [key: number]: boolean }>({});

  const { updateRows, rows } = useAgGridClientTableContext();

  useEffect(() => {
    if (!fetchExternalInventoryCliJob?.id) {
      return;
    }

    setEnabledJobs((prevValues) => ({
      ...prevValues,
      [fetchExternalInventoryCliJob?.id]: true,
    }));
  }, [fetchExternalInventoryCliJob?.id]);

  useEffect(() => {
    if (!Object.values(enabledJobs).includes(true)) {
      return;
    }

    const updatedRows = rows
      .filter(
        ({ corporateLocation, inventoryStatus }) =>
          !inventoryStatus?.isLoading &&
          !inventoryStatus?.error &&
          [null, undefined].includes(inventoryStatus?.totalQty) &&
          !corporateLocation?.isLoading &&
          !corporateLocation?.error &&
          // Only an absent quantity means "not fetched yet": the location dropdown writes its
          // selected record onto the row, and there null means fetched-but-unconvertible.
          corporateLocation?.inventoryTotalQty === undefined
      )
      .map((row) => ({
        ...row,
        inventoryStatus: {
          ...row.inventoryStatus,
          isLoading: true,
        },
        corporateLocation: {
          ...row.corporateLocation,
          isLoading: true,
        },
      }));

    updateRows({ update: updatedRows }, { refreshCells: true });
  }, [enabledJobs, rows, updateRows]);

  const onFetchExternalInventorySuccess = ({ record }) => {
    setEnabledJobs((prevValues) => ({
      ...prevValues,
      [record.id]: false,
    }));

    const inventory = record.result;

    const updatedRows = rows
      .filter((row) => Object.keys(inventory).includes(row.id.toString()))
      .map((row) => mapInventoryData(row, inventory));

    updateRows({ update: updatedRows }, { refreshCells: true });
  };

  const onFetchExternalInventoryError = (data) => {
    const cliJob = data.record;

    setEnabledJobs((prevValues) => ({
      ...prevValues,
      [cliJob?.id]: false,
    }));

    const updatedRows = rows
      .filter(({ inventoryStatus }) => inventoryStatus.isLoading)
      .map((row) => {
        return {
          ...row,
          inventoryStatus: {
            isLoading: false,
            error: cliJob.statusDescription,
          },
          corporateLocation: {
            isLoading: false,
            error: cliJob.statusDescription,
          },
        };
      });

    updateRows({ update: updatedRows }, { refreshCells: true });
  };

  const queries: ICliJobQuery[] = useMemo(() => {
    return Object.keys(enabledJobs).map((jobId) => {
      return {
        queryKey: 'cliJob',
        requestPayload: { job: Number(jobId) },
        enabled: enabledJobs[jobId],
      };
    });
  }, [enabledJobs]);

  useCliJobsChecker({
    queries,
    onSuccess: onFetchExternalInventorySuccess,
    onError: onFetchExternalInventoryError,
  });

  const mapInventoryData = (row: AgGridDataRow, inventory: IQuotePartInventory) => {
    const rowInventory = inventory?.[row.id];

    if (!rowInventory) {
      return {
        ...row,
        inventoryStatus: {
          ...row.inventoryStatus,
          isLoading: false,
        },
        corporateLocation: {
          ...row.corporateLocation,
          isLoading: false,
        },
      };
    }

    const corporateLocation = !row.corporateLocationId
      ? { isLoading: false }
      : {
          ...row.corporateLocation,
          ...rowInventory.inventoryAtLocation,
          isLoading: false,
        };

    return {
      ...row,
      inventoryStatus: {
        ...rowInventory.inventoryStatus,
        isLoading: false,
      },
      corporateLocation,
    };
  };
};
